import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchAuthSession, signOut as amplifySignOut } from '@aws-amplify/auth';
import { clearLocalAuthTokens } from '../lib/auth';

export type UserRole = 'internal' | 'external' | 'admin' | 'event-manager';

export interface AuthUser {
  sub: string;
  email: string;
  groups: UserRole[];
  idToken: string;
  accessToken: string;
  /** ISO 8601 expiry of the current tokens */
  expiresAt: string;
}

export interface AuthState {
  user: AuthUser | null;
  /** Whether the initial auth check has completed */
  initialized: boolean;
  loading: boolean;
  error: string | null;
  // Derived convenience flags (computed from user.groups)
  isAuthenticated: boolean;
  isInternal: boolean;
  isExternal: boolean;
  isAdmin: boolean;
  isEventManager: boolean;
}

const initialState: AuthState = {
  user: null,
  initialized: false,
  loading: false,
  error: null,
  isAuthenticated: false,
  isInternal: false,
  isExternal: false,
  isAdmin: false,
  isEventManager: false,
};

/** Derive boolean flags from a user's group list */
function deriveFlags(groups: UserRole[]) {
  return {
    isAuthenticated: true,
    isAdmin: groups.includes('admin'),
    isInternal: groups.some((g) => g === 'internal' || g === 'admin' || g === 'event-manager'),
    isExternal: groups.includes('external'),
    isEventManager: groups.includes('event-manager'),
  };
}

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Called on app startup to restore an existing Cognito session.
 * First tries to read tokens from localStorage (set during OAuth callback),
 * then falls back to Amplify's fetchAuthSession for non-OAuth flows.
 */
export const initializeAuth = createAsyncThunk('auth/initialize', async () => {
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
  const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;

  // Try reading tokens from localStorage (our manual OAuth flow stores them here)
  const lastUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
  if (lastUser) {
    const idToken = localStorage.getItem(`${keyPrefix}.${lastUser}.idToken`);
    const accessToken = localStorage.getItem(`${keyPrefix}.${lastUser}.accessToken`);

    if (idToken && accessToken) {
      try {
        const claims = JSON.parse(atob(idToken.split('.')[1]));
        const sub = (claims['sub'] as string) ?? '';
        const email = (claims['email'] as string) ?? '';
        const rawGroups: string[] = (claims['cognito:groups'] as string[]) ?? [];
        const groups = rawGroups.filter((g): g is UserRole =>
          ['internal', 'external', 'admin', 'event-manager'].includes(g),
        );

        const exp = claims['exp'] as number | undefined;
        // Check if token is expired
        if (exp && exp * 1000 < Date.now()) {
          // Token expired — clear and return null
          localStorage.removeItem(`${keyPrefix}.LastAuthUser`);
          localStorage.removeItem(`${keyPrefix}.${lastUser}.idToken`);
          localStorage.removeItem(`${keyPrefix}.${lastUser}.accessToken`);
          localStorage.removeItem(`${keyPrefix}.${lastUser}.refreshToken`);
          return null;
        }

        const expiresAt = exp ? new Date(exp * 1000).toISOString() : '';

        return {
          sub,
          email,
          groups,
          idToken,
          accessToken,
          expiresAt,
        } as AuthUser;
      } catch {
        // Invalid token format — fall through
      }
    }
  }

  // Fallback: try Amplify's fetchAuthSession (works for email/password sign-in)
  // Skip on the /callback path where fetchAuthSession hangs
  if (window.location.pathname.startsWith('/callback')) {
    return null;
  }
  try {
    const session = await fetchAuthSession();
    const idTokenObj = session.tokens?.idToken;
    const accessTokenObj = session.tokens?.accessToken;

    if (!idTokenObj || !accessTokenObj) {
      return null;
    }

    const claims = idTokenObj.payload;
    const sub = (claims['sub'] as string) ?? '';
    const email = (claims['email'] as string) ?? '';
    const rawGroups: string[] = (claims['cognito:groups'] as string[]) ?? [];
    const groups = rawGroups.filter((g): g is UserRole =>
      ['internal', 'external', 'admin', 'event-manager'].includes(g),
    );

    const exp = accessTokenObj.payload['exp'] as number | undefined;
    const expiresAt = exp ? new Date(exp * 1000).toISOString() : '';

    return {
      sub,
      email,
      groups,
      idToken: idTokenObj.toString(),
      accessToken: accessTokenObj.toString(),
      expiresAt,
    } as AuthUser;
  } catch {
    return null;
  }
});

/**
 * Signs the user out via Amplify and clears the auth state.
 *
 * Retained for backward compatibility with callers that drive sign-out through
 * Amplify. In the current flow the canonical federated sign-out is owned by
 * `completeFederatedSignOut()` in ../lib/auth (token clearing + /logout redirect)
 * and Redux state is reset by the `clearUser` reducer. This thunk additionally
 * calls `clearLocalAuthTokens()` so the manually-stored OAuth tokens are cleared
 * even on the Amplify path (Requirement 3.1).
 */
export const signOut = createAsyncThunk('auth/signOut', async () => {
  await amplifySignOut();
  // Clear the manual OAuth tokens in localStorage so stale tokens never linger
  // after an Amplify-driven sign-out.
  clearLocalAuthTokens();
});

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.initialized = true;
      state.loading = false;
      state.error = null;
      Object.assign(state, deriveFlags(action.payload.groups));
    },
    clearUser(state) {
      state.user = null;
      state.initialized = true;
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;
      state.isInternal = false;
      state.isExternal = false;
      state.isAdmin = false;
      state.isEventManager = false;
    },
    setAuthLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setAuthError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },
    setInitialized(state) {
      state.initialized = true;
    },
  },
  extraReducers: (builder) => {
    // initializeAuth
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        if (action.payload) {
          state.user = action.payload;
          Object.assign(state, deriveFlags(action.payload.groups));
        } else {
          state.user = null;
          state.isAuthenticated = false;
          state.isInternal = false;
          state.isExternal = false;
          state.isAdmin = false;
          state.isEventManager = false;
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        // Session not found or expired — treat as unauthenticated
        state.loading = false;
        state.initialized = true;
        state.user = null;
        state.isAuthenticated = false;
        state.isInternal = false;
        state.isExternal = false;
        state.isAdmin = false;
        state.isEventManager = false;
      });

    // signOut
    builder
      .addCase(signOut.pending, (state) => {
        state.loading = true;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.user = null;
        state.initialized = true;
        state.loading = false;
        state.error = null;
        state.isAuthenticated = false;
        state.isInternal = false;
        state.isExternal = false;
        state.isAdmin = false;
        state.isEventManager = false;
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setUser, clearUser, setAuthLoading, setAuthError, setInitialized } = authSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectIsAdmin = (state: { auth: AuthState }) => state.auth.isAdmin;
export const selectIsInternal = (state: { auth: AuthState }) => state.auth.isInternal;
export const selectIsExternal = (state: { auth: AuthState }) => state.auth.isExternal;
export const selectIsEventManager = (state: { auth: AuthState }) => state.auth.isEventManager;
export const selectAuthInitialized = (state: { auth: AuthState }) => state.auth.initialized;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;

export default authSlice.reducer;
